import { useState } from "react";

const PANEL_COUNT = 9;
const SIDE = 36;
const H = (SIDE * Math.sqrt(3)) / 2;
const H3 = H / 3;
const H23 = (2 * H) / 3;

/** Equilateral triangle: up — bottom at cy+H3, top at cy-H23 */
function upTriangle(cx: number, cy: number) {
  return `${cx - SIDE/2},${cy + H3} ${cx + SIDE/2},${cy + H3} ${cx},${cy - H23}`;
}

/** Equilateral triangle: down — top at cy-H3, bottom at cy+H23 */
function downTriangle(cx: number, cy: number) {
  return `${cx - SIDE/2},${cy - H3} ${cx + SIDE/2},${cy - H3} ${cx},${cy + H23}`;
}

/**
 * 9 panels in W-shaped zig-zag, mirrored left/right. Tessellation rules:
 * up above down: cy_up = cy_down - H23
 * down below up: cy_down = cy_up + H23
 * down right of up: (cx+S/2, cy-H3), up right of down: (cx+S/2, cy+H3)
 */
const OX = 84;
const OY = 80;

const PANELS: { points: string }[] = [
  // left column (mirrors right)
  { points: downTriangle(0 + OX, H23 + H + OY) },
  { points: upTriangle(0 + OX, H + OY) },
  // middle-left (mirrors middle-right)
  { points: downTriangle(SIDE/2 + OX, H23 + OY) },
  { points: upTriangle(SIDE/2 + OX, 0 + OY) },
  // center peak
  { points: downTriangle(SIDE + OX, -H3 + OY) },
  // middle-right
  { points: upTriangle(3*SIDE/2 + OX, 0 + OY) },
  { points: downTriangle(3*SIDE/2 + OX, H23 + OY) },
  // right column
  { points: upTriangle(2*SIDE + OX, H + OY) },
  { points: downTriangle(2*SIDE + OX, H23 + H + OY) },
];

const COLOR_GREEN = "#22c55e";
const COLOR_ORANGE = "#f97316";
const COLOR_RED = "#ef4444";
const COLOR_DIM = "#1e293b";

/** minutes 0 = none lit, 10 = all lit */
function getIlluminatedCount(minutes: number): number {
  if (minutes <= 0) return 0;
  if (minutes >= 10) return PANEL_COUNT;
  return Math.round((minutes / 10) * PANEL_COUNT);
}

function getColorForState(illuminatedCount: number): string {
  if (illuminatedCount === 0) return COLOR_DIM;
  if (illuminatedCount >= 8) return COLOR_GREEN;
  if (illuminatedCount >= 4) return COLOR_ORANGE;
  return COLOR_RED;
}

interface MetroinfoNanoleafProps {
  /** 0 = all lit, 1 = none lit - used when scrolling (overrides minutes when set) */
  scrollProgress?: number;
  /** Compact card mode: SVG only, no slider */
  variant?: "full" | "card";
}

export function MetroinfoNanoleaf({ scrollProgress, variant = "full" }: MetroinfoNanoleafProps) {
  const [minutes, setMinutes] = useState(8);

  const useScroll = scrollProgress !== undefined;
  const illuminatedCount = useScroll
    ? Math.round((1 - scrollProgress) * PANEL_COUNT)
    : getIlluminatedCount(minutes);
  const displayMinutes = useScroll ? Math.round((1 - scrollProgress) * 10) : minutes;
  const fillColor = getColorForState(illuminatedCount);

  const svg = (
    <svg
      viewBox="0 0 240 180"
      className={variant === "card" ? "h-full w-full object-cover" : "w-full max-w-[280px]"}
      aria-label="Nanoleaf bus countdown display"
    >
      {PANELS.map((panel, i) => {
        const lit = i < illuminatedCount;
        return (
          <polygon
            key={i}
            points={panel.points}
            fill={lit ? fillColor : COLOR_DIM}
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="1"
            className="transition-colors duration-500 ease-out"
          />
        );
      })}
    </svg>
  );

  if (variant === "card") {
    return <div className="flex h-full w-full items-center justify-center bg-panel">{svg}</div>;
  }

  return (
    <div className="mb-4">
      <div className="flex flex-col items-center gap-4">
        {svg}
        <div className="w-full max-w-[280px]">
          <p className="block font-mono text-xs text-text-muted text-center tabular-nums">
            Minutes until bus: {displayMinutes}
          </p>
        </div>
      </div>
    </div>
  );
}
